<template>
  <div class="packs">
    <header>
      <h1>{{ t('packs.title') }}</h1>
    </header>

    <section>
      <h2>{{ t('packs.yours') }}</h2>
      <p v-if="ownedPacks.length === 0" class="empty">
        {{ t('packs.empty') }}
      </p>
      <div class="row">
        <article v-for="item in ownedPacks" :key="item.def.id" class="tile">
          <PackCover
            :src="item.def.cover"
            :alt="item.def.name"
            size="md"
            :complete="completeLabel(item.def.id)"
          />
          <div class="meta">
            <h3>{{ packTitle(item.def.id, item.def.name) }}</h3>
            <p>{{ t(`packs.tagline.${item.def.id}`) }}</p>
            <p
              class="complete-line"
              :aria-label="completeAria(item.def.id)"
            >
              {{ completeOf(item.def.id) }}
            </p>
            <p class="qty">x{{ item.count }}</p>
            <button
              type="button"
              class="list-btn"
              @click.stop="openList(item.def.id)"
            >
              {{ t('packs.contents') }}
            </button>
            <div class="open-row">
              <button
                type="button"
                class="cta"
                :disabled="opening"
                @click.stop="openOwned(item.def.id, 1)"
              >
                {{ t('packs.open') }}
              </button>
              <button
                v-if="item.count >= 3"
                type="button"
                class="cta ghost"
                :disabled="opening"
                @click.stop="openOwned(item.def.id, 3)"
              >
                {{ t('packs.openThree') }}
              </button>
              <button
                v-if="item.count >= 5"
                type="button"
                class="cta ghost"
                :disabled="opening"
                @click.stop="openOwned(item.def.id, 5)"
              >
                {{ t('packs.openFive') }}
              </button>
              <button
                v-if="item.count >= 10"
                type="button"
                class="cta ghost"
                :disabled="opening"
                @click.stop="openOwned(item.def.id, 10)"
              >
                {{ t('packs.openTen') }}
              </button>
            </div>
          </div>
        </article>
      </div>
    </section>

    <section>
      <h2>{{ t('packs.shop') }}</h2>
      <p class="note">{{ t('packs.shopNote') }}</p>
      <div class="row">
        <article
          v-for="shop in shopPacks"
          :key="shop.id"
          class="tile shop"
          :class="{ 'just-bought': boughtId === shop.id && boughtQty > 0 }"
        >
          <PackCover
            :src="shop.cover"
            :alt="shop.name"
            size="md"
            :complete="completeLabel(shop.id)"
          />
          <div class="meta">
            <h3>{{ packTitle(shop.id, shop.name) }}</h3>
            <p>{{ t(`packs.tagline.${shop.id}`) }}</p>
            <p
              class="complete-line"
              :aria-label="completeAria(shop.id)"
            >
              {{ completeOf(shop.id) }}
            </p>
            <button
              type="button"
              class="list-btn"
              @click.stop="openList(shop.id)"
            >
              {{ t('packs.contents') }}
            </button>
            <p class="qty price-row">
              <CoinToken size="md" />
              <span class="price-x">×{{ packPrice }}</span>
            </p>
            <p
              :key="`${shop.id}-${haveBumpKey}`"
              class="qty have-line"
              :class="{ bump: boughtId === shop.id && haveBumpKey > 0 }"
            >
              {{ t('packs.have', { count: collection.packs[shop.id] ?? 0 }) }}
            </p>
            <div class="open-row">
              <button
                type="button"
                class="cta ghost"
                :disabled="wallet.busy"
                @click="buy(shop.id, 1)"
              >
                {{ buyLabel }}
              </button>
              <button
                v-if="canAfford(3)"
                type="button"
                class="cta ghost"
                :disabled="wallet.busy"
                @click="buy(shop.id, 3)"
              >
                {{ t('packs.buyThree') }}
              </button>
              <button
                v-if="canAfford(5)"
                type="button"
                class="cta ghost"
                :disabled="wallet.busy"
                @click="buy(shop.id, 5)"
              >
                {{ t('packs.buyFive') }}
              </button>
              <button
                v-if="canAfford(10)"
                type="button"
                class="cta ghost"
                :disabled="wallet.busy"
                @click="buy(shop.id, 10)"
              >
                {{ t('packs.buyTen') }}
              </button>
            </div>
          </div>
        </article>
      </div>
    </section>

    <div
      v-if="listPack"
      class="open-failed"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="listTitleId"
      @click.self="closeList"
    >
      <div class="list-card">
        <header class="list-head">
          <h2 :id="listTitleId">{{ listTitle }}</h2>
          <button type="button" class="list-btn" @click="closeList">
            {{ t('packs.done') }}
          </button>
        </header>
        <p v-if="listNote" class="list-note">{{ listNote }}</p>
        <p v-if="listProgress" class="list-count">
          {{ t('packs.contentsProgress', listProgress) }}
        </p>
        <input
          v-model="listQuery"
          class="list-search"
          type="search"
          :placeholder="t('packs.contentsSearch')"
        />
        <p v-if="listQuery.trim()" class="list-count">
          {{ t('packs.contentsCount', { n: listVisible }) }}
        </p>
        <div class="list-scroll">
          <section v-for="group in listGroups" :key="group.rarity">
            <h3>
              {{ t(`packs.rarity.${group.rarity}`) }}
              · {{ group.owned }}/{{ group.cards.length }}
            </h3>
            <ul>
              <li
                v-for="card in group.cards"
                :key="card.id"
                :class="card.owned ? 'have' : 'need'"
              >
                <span>{{ card.name }}</span>
                <span
                  v-if="card.owned"
                  class="have-mark"
                  :aria-label="t('packs.haveCard')"
                >✓</span>
              </li>
            </ul>
          </section>
          <p v-if="listGroups.length === 0" class="list-note">
            {{ t('packs.contentsEmpty') }}
          </p>
        </div>
      </div>
    </div>

    <PackOpeningOverlay
      v-if="session"
      :key="sessionNonce"
      :pack="session.pack"
      :batches="session.batches"
      :remaining="remaining"
      @close="closeSession"
      @open-more="openMore"
    />

    <Transition name="buy-toast">
      <button
        v-if="buyNotice"
        type="button"
        class="buy-toast"
        :class="buyNotice.tone"
        :aria-live="buyNotice.tone === 'ok' ? 'polite' : 'assertive'"
        @click="dismissBuyNotice"
      >
        {{ buyNotice.text }}
      </button>
    </Transition>

    <output
      v-if="opening"
      class="opening-veil"
      aria-live="polite"
    >
      {{ t('packs.opening') }}
    </output>

    <div
      v-else-if="openFailed"
      class="open-failed"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="pack-open-failed"
    >
      <div class="open-failed-card">
        <p id="pack-open-failed">{{ t('packs.openFailed') }}</p>
        <button type="button" class="cta" @click="openFailed = false">
          {{ t('packs.done') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onUnmounted, ref, watch } from 'vue'
import { SHOP_PACK_ID, SHOP_PACK_PRICE, SHOP_SELL_IDS, type PrintedRarity } from '@shared/index'
import { useCollectionStore } from '../stores/collection'
import { useWalletStore } from '../stores/wallet'
import {
  PACK_BY_ID,
  PACKS,
  packListEntries,
  type PackId,
  type PackPull,
} from '../game/packs'
import { localizedName } from '../i18n/localizeCard'
import PackOpeningOverlay from '../components/PackOpeningOverlay.vue'
import PackCover from '../components/PackCover.vue'
import CoinToken from '../components/CoinToken.vue'
import { PackSfx } from '../game/audio/PackSfx'
import { track } from '../analytics/track'
import { useI18n } from '../i18n/t'

const { t } = useI18n()
const collection = useCollectionStore()
const wallet = useWalletStore()

const packPrice = SHOP_PACK_PRICE
const canAfford = (qty: number) => wallet.coins >= packPrice * qty
const buyLabel = computed(() =>
  wallet.busy ? t('packs.buying') : t('packs.buy'),
)

const shopPacks = SHOP_SELL_IDS.map((id) => PACK_BY_ID[id])

function packTitle(id: string, fallback: string): string {
  const key = `packs.names.${id}`
  const label = t(key)
  return label === key ? fallback : label
}

type PackComplete = { owned: number; total: number; percent: number }

function clampPercent(owned: number, total: number): number {
  if (total <= 0 || owned <= 0) return 0
  if (owned >= total) return 100
  return Math.min(99, Math.max(1, Math.round((owned / total) * 100)))
}

const packComplete = computed((): Record<PackId, PackComplete> => {
  const out = {} as Record<PackId, PackComplete>
  for (const pack of PACKS) {
    const rows = packListEntries(pack.id)
    const total = rows.length
    const owned = rows.filter((row) => collection.seen(row.id)).length
    out[pack.id] = {
      owned,
      total,
      percent: clampPercent(owned, total),
    }
  }
  return out
})

function completeLabel(id: PackId): string {
  return t('packs.complete', { percent: packComplete.value[id].percent })
}

function completeOf(id: PackId): string {
  const row = packComplete.value[id]
  return t('packs.completeOf', { owned: row.owned, total: row.total })
}

function completeAria(id: PackId): string {
  const row = packComplete.value[id]
  return t('packs.completeAria', row)
}

const session = ref<{
  pack: (typeof PACKS)[number]
  batches: PackPull[][]
} | null>(null)
const sessionNonce = ref(0)
const opening = ref(false)
const openFailed = ref(false)
const boughtQty = ref(0)
const boughtId = ref<PackId | null>(null)
const haveBumpKey = ref(0)
type BuyTone = 'ok' | 'warn' | 'error'

const buyNotice = ref<{ tone: BuyTone; text: string } | null>(null)
let buyNoticeTimer = 0

const buyFailNotice = (code: string): { tone: BuyTone; text: string } => {
  if (code === 'insufficient_coins') {
    return { tone: 'warn', text: t('packs.buyNeedCoins') }
  }
  if (code === 'rate_limited') {
    return { tone: 'warn', text: t('packs.buyRate') }
  }
  if (
    code === 'signed_out' ||
    code === 'Missing auth token' ||
    code === 'Invalid or expired token'
  ) {
    return { tone: 'warn', text: t('packs.buySignedOut') }
  }
  return { tone: 'error', text: t('packs.buyFailed') }
}

const dismissBuyNotice = () => {
  window.clearTimeout(buyNoticeTimer)
  buyNotice.value = null
  boughtQty.value = 0
}

const showBuyNotice = (tone: BuyTone, text: string) => {
  window.clearTimeout(buyNoticeTimer)
  buyNotice.value = { tone, text }
  buyNoticeTimer = window.setTimeout(dismissBuyNotice, 4200)
}

const showBought = (id: PackId, qty: number) => {
  boughtId.value = id
  boughtQty.value = qty
  haveBumpKey.value += 1
  showBuyNotice(
    'ok',
    qty === 1 ? t('packs.boughtOne') : t('packs.boughtMany', { n: qty }),
  )
}

const closeSession = () => {
  session.value = null
}

watch(
  () => opening.value || session.value !== null,
  (active) => collection.setPackCeremony(active),
  { immediate: true },
)

onUnmounted(() => {
  collection.setPackCeremony(false)
  window.clearTimeout(buyNoticeTimer)
  window.removeEventListener('keydown', onListKey)
})

const ownedPacks = computed(() =>
  PACKS.map((def) => ({
    def,
    count: collection.packs[def.id] ?? 0,
  })).filter((item) => item.count > 0),
)

const remaining = computed(() => {
  if (!session.value) return 0
  return collection.packs[session.value.pack.id] ?? 0
})

const startSession = async (id: PackId, qty: number) => {
  if (opening.value) return
  opening.value = true
  openFailed.value = false
  PackSfx.play('whoosh')
  const batches = await collection.openPacks(id, qty)
  if (!batches) {
    opening.value = false
    openFailed.value = true
    return
  }
  session.value = null
  sessionNonce.value += 1
  await nextTick()
  session.value = {
    pack: { ...PACK_BY_ID[id], name: packTitle(id, PACK_BY_ID[id].name) },
    batches,
  }
  await nextTick()
  opening.value = false
}

const LIST_ORDER: PrintedRarity[] = ['secret', 'ultra', 'super', 'rare', 'common']
const listPack = ref<PackId | null>(null)
const listQuery = ref('')
const listTitleId = 'pack-contents-title'

const listTitle = computed(() => {
  if (!listPack.value) return ''
  const def = PACK_BY_ID[listPack.value]
  return packTitle(def.id, def.name)
})

const listNote = computed(() => {
  if (!listPack.value) return ''
  if (listPack.value === SHOP_PACK_ID) return t('packs.contentsMaster')
  return t('packs.contentsChase')
})

const listProgress = computed(() => {
  if (!listPack.value) return null
  return packComplete.value[listPack.value]
})

const listGroups = computed(() => {
  if (!listPack.value) return []
  const query = listQuery.value.trim().toLowerCase()
  const rows = packListEntries(listPack.value)
    .map((entry) => ({
      id: entry.id,
      rarity: entry.rarity,
      name: localizedName(entry.id, entry.id),
      owned: collection.seen(entry.id),
    }))
    .filter((entry) => !query || entry.name.toLowerCase().includes(query))
    .sort((a, b) => a.name.localeCompare(b.name))
  return LIST_ORDER.map((rarity) => {
    const cards = rows.filter((entry) => entry.rarity === rarity)
    return {
      rarity,
      cards,
      owned: cards.filter((card) => card.owned).length,
    }
  }).filter((group) => group.cards.length > 0)
})

const listVisible = computed(() =>
  listGroups.value.reduce((sum, group) => sum + group.cards.length, 0),
)

const openList = (id: PackId) => {
  listQuery.value = ''
  listPack.value = id
}

const closeList = () => {
  listPack.value = null
}

const onListKey = (event: KeyboardEvent) => {
  if (event.key === 'Escape') closeList()
}

watch(listPack, (id) => {
  if (id) window.addEventListener('keydown', onListKey)
  else window.removeEventListener('keydown', onListKey)
})

const openOwned = (id: PackId, qty: number) => {
  void startSession(id, qty)
}

const openMore = (qty: number) => {
  if (!session.value) return
  void startSession(session.value.pack.id, qty)
}

const showBuyFail = (code: string) => {
  boughtQty.value = 0
  const notice = buyFailNotice(code)
  showBuyNotice(notice.tone, notice.text)
}

const buy = async (id: PackId, qty: number) => {
  if (wallet.busy) return
  const result = await wallet.buyShopPack(id, qty)
  if (result.ok) {
    PackSfx.play('whoosh')
    showBought(id, qty)
    return
  }
  showBuyFail(result.code)
  if (result.sent || result.code === 'busy') return
  track('pack_buy_refused', {
    packId: id,
    qty,
    error: result.code,
    coins: wallet.coins,
  })
}
</script>

<style scoped>
.packs {
  max-width: 960px;
  margin: 0 auto;
  padding: 32px 20px 64px;
  color: var(--md-sys-color-on-surface);
  font-family: Roboto, system-ui, sans-serif;
}

h1 {
  font-size: 36px;
  font-weight: 400;
  color: var(--md-sys-color-primary);
  margin: 0 0 20px;
}

.note,
.empty {
  color: var(--md-sys-color-on-surface-variant);
  margin: 8px 0 20px;
}

h2 {
  font-size: 22px;
  margin-bottom: 12px;
}

section {
  margin-bottom: 36px;
}

.row {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.tile {
  display: flex;
  align-items: center;
  gap: 16px;
  width: min(440px, 100%);
  padding: 14px 16px 14px 12px;
  border-radius: 12px;
  background: var(--md-sys-color-surface-container);
  box-shadow: var(--md-elevation-1);
  border: 1px solid color-mix(in srgb, var(--md-sys-color-primary) 22%, transparent);
}

.tile.shop {
  width: min(440px, 100%);
}

.tile :deep(.booster) {
  flex-shrink: 0;
}

.meta h3 {
  color: var(--md-sys-color-on-surface);
  margin-bottom: 4px;
}

.meta p {
  color: var(--md-sys-color-on-surface-variant);
  font-size: 13px;
}

.complete-line {
  color: var(--md-sys-color-primary) !important;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  margin: 6px 0 0;
}

.qty {
  color: var(--md-sys-color-primary) !important;
  font-weight: 700;
  margin: 8px 0;
}

.price-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.price-x {
  font-variant-numeric: tabular-nums;
  font-size: 18px;
}

.have-line {
  width: fit-content;
  transform-origin: left center;
}

.have-line.bump {
  animation: have-bump 600ms var(--md-motion-emphasized);
}

.tile.shop.just-bought {
  border-color: var(--md-sys-color-primary);
  box-shadow: var(--md-elevation-2);
}

@keyframes have-bump {
  0% { transform: scale(1); }
  40% { transform: scale(1.16); }
  100% { transform: scale(1); }
}

.buy-toast {
  position: fixed;
  left: 50%;
  top: calc(var(--app-nav-offset, 64px) + 16px);
  z-index: 80;
  width: min(720px, calc(100vw - 48px));
  min-height: 72px;
  margin: 0;
  padding: 20px 32px;
  border: 0;
  border-radius: 16px;
  font: inherit;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.3;
  text-align: center;
  cursor: pointer;
  transform: translateX(-50%);
  box-shadow: var(--md-elevation-2);
}

/* Status fills. These stay the same in light and dark. */
.buy-toast.ok {
  background: #1b7f3a;
  color: #f4fff6;
}

.buy-toast.warn {
  background: #f5c518;
  color: #1c1400;
}

.buy-toast.error {
  background: #ba1a1a;
  color: #ffffff;
}

.buy-toast-enter-active,
.buy-toast-leave-active {
  transition:
    opacity var(--md-motion-short) var(--md-motion-standard),
    transform var(--md-motion-short) var(--md-motion-standard);
}

.buy-toast-enter-from,
.buy-toast-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(-12px);
}

@media (max-width: 720px) {
  .buy-toast {
    top: auto;
    bottom: calc(88px + env(safe-area-inset-bottom, 0px));
    width: min(440px, calc(100vw - 24px));
    min-height: 56px;
    padding: 16px 20px;
    font-size: 17px;
  }

  .buy-toast-enter-from,
  .buy-toast-leave-to {
    transform: translateX(-50%) translateY(12px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .have-line.bump {
    animation: none;
  }

  .buy-toast-enter-active,
  .buy-toast-leave-active {
    transition: none;
  }
}

.cta {
  padding: 8px 14px;
  border: none;
  border-radius: 8px;
  background: var(--md-sys-color-primary);
  color: var(--md-sys-color-on-primary);
  font-weight: 700;
  cursor: pointer;
}

.open-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.cta:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.cta.ghost {
  background: transparent;
  border: 1px solid var(--md-sys-color-primary);
  color: var(--md-sys-color-primary);
}

.cta,
.cta.ghost {
  min-height: 44px;
}

.opening-veil,
.open-failed {
  position: fixed;
  inset: 0;
  z-index: 10100;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 24px;
  text-align: center;
}

.opening-veil {
  margin: 0;
  border: 0;
  background:
    radial-gradient(circle at 50% 18%, rgba(250, 204, 21, 0.16), transparent 42%),
    radial-gradient(circle at 50% 80%, rgba(30, 58, 138, 0.35), transparent 50%),
    #070714;
  color: #e2e8f0;
  font-size: 22px;
}

.open-failed {
  z-index: 10100;
  background: var(--md-scrim);
}

.open-failed-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: min(360px, 100%);
  padding: 20px 24px;
  border-radius: 12px;
  background: var(--md-sys-color-surface-container);
  color: var(--md-sys-color-on-surface);
  box-shadow: var(--md-elevation-2);
}

.open-failed-card p {
  margin: 0;
}

.list-btn {
  align-self: flex-start;
  margin: 0 0 8px;
  padding: 0;
  border: none;
  background: none;
  color: var(--md-sys-color-primary);
  font: inherit;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}

.list-btn:hover {
  text-decoration: underline;
}

.list-card {
  display: flex;
  flex-direction: column;
  width: min(520px, 100%);
  max-height: min(80vh, 720px);
  padding: 20px 24px;
  border-radius: 12px;
  background: var(--md-sys-color-surface-container);
  color: var(--md-sys-color-on-surface);
  box-shadow: var(--md-elevation-2);
  text-align: left;
}

.list-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.list-head h2 {
  margin: 0;
  font-size: 22px;
  font-weight: 400;
}

.list-head .list-btn {
  margin: 0;
}

.list-note,
.list-count {
  margin: 8px 0 0;
  color: var(--md-sys-color-on-surface-variant);
  font-size: 13px;
}

.list-search {
  width: 100%;
  box-sizing: border-box;
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid var(--md-sys-color-outline);
  background: var(--md-sys-color-surface);
  color: var(--md-sys-color-on-surface);
  font: inherit;
}

.list-scroll {
  margin-top: 12px;
  overflow: auto;
}

.list-scroll h3 {
  margin: 16px 0 6px;
  font-size: 13px;
  font-weight: 700;
  color: var(--md-sys-color-primary);
}

.list-scroll ul {
  margin: 0;
  padding: 0;
  list-style: none;
}

.list-scroll li {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  padding: 6px 0;
  border-bottom: 1px solid var(--md-sys-color-outline-variant);
  font-size: 14px;
}

.list-scroll li.need {
  color: var(--md-sys-color-on-surface-variant);
}

.have-mark {
  flex-shrink: 0;
  color: var(--md-sys-color-primary);
  font-weight: 700;
}

@media (max-width: 720px) {
  .packs {
    padding: 20px 16px 48px;
  }

  h1 {
    font-size: 28px;
  }

  .tile {
    width: 100%;
  }
}

@media (max-height: 500px) and (max-width: 1100px) {
  .packs {
    padding: 12px 16px 32px;
  }

  h1 {
    font-size: 24px;
  }

  h2 {
    font-size: 18px;
    margin-bottom: 8px;
  }

  section {
    margin-bottom: 20px;
  }

  .row {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }

  .tile,
  .tile.shop {
    width: 100%;
    padding: 10px 12px;
  }
}
</style>
